import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as LoginActions from './login.actions';
import { LoginService } from './login.service';
import { ArkikAlertService } from 'src/app/shared/components/arkik-alert/arkik-alert.service';
import { map, switchMap, catchError, withLatestFrom, tap, takeUntil } from 'rxjs/operators';
import { defer, from, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { selectLoginState } from './login.selectors';
import { CookieService } from 'ngx-cookie-service';
import { environment } from 'src/environments/environment';
import { UpdateCommonDataService } from 'src/app/core/services/common/updatecommondata.service';
import { ManageIdbService } from 'src/app/idb/manage-idb.service';
import { IAuthenticatedModel } from './login.models';
import { CatalogLogoutService } from '@core/services/common/catalog-logout.service';

@Injectable()
export class LoginEffects {
  constructor(
    private actions$: Actions,
    private loginService: LoginService,
    private alertService: ArkikAlertService,
    private store: Store,
    private cookieService: CookieService,
    private updateCommonService: UpdateCommonDataService,
    private idb: ManageIdbService,
    private catalogLogout: CatalogLogoutService,
  ) {}

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoginActions.loadLogin),
      withLatestFrom(this.store.select(selectLoginState)),
      switchMap(([{ payload }, state]) => {
        const email = payload.UserName;
        const epoch = this.idb.catalogEpoch;
        return defer(() => {
          const invalidated = this.idb.cacheInvalidated;
          return from(this.idb.prepareCatalogAccess(epoch)).pipe(
            switchMap(() => {
              const cached = invalidated ? undefined : state?.authenticated?.[email];
              return cached ? of(cached) : from(this.idb.selectAuthSession(email));
            }),
          );
        }).pipe(
          switchMap((cachedResponse) => {
            if (cachedResponse) {
              this.applyAuthenticatedSession(cachedResponse);
              return of(LoginActions.loginSuccess({ email, response: cachedResponse }));
            }

            return this.loginService.login(payload).pipe(
              switchMap((response: IAuthenticatedModel) => {
                this.applyAuthenticatedSession(response);
                return from(this.idb.saveAuthSession(email, response, epoch)).pipe(map(() => response));
              }),
              map((response: IAuthenticatedModel) => LoginActions.loginSuccess({ email, response })),
            );
          }),
          catchError((error: unknown) => {
            console.error('Authenticated session/cache access failed', error);
            this.alertService.error('views.globals.serverError');
            return of(LoginActions.loginFailure({ error: 'views.globals.serverError' }));
          }),
          takeUntil(this.idb.catalogCancellation$),
        );
      }),
    ),
  );

  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LoginActions.logout),
        tap(() => {
          // Limpia cookies y otros datos relacionados al usuario
          this.cookieService.delete(environment.UserGuid);
          this.cookieService.delete(environment.BusinessPlanGuid);
          this.cookieService.delete(environment.ProductGuid);
          this.cookieService.delete(environment.CompanyGuid);
          this.catalogLogout
            .start()
            .catch((error: unknown) => console.error('Logout cache invalidation failed', error));
        }),
      ),
    { dispatch: false },
  );

  private applyAuthenticatedSession(response: IAuthenticatedModel): void {
    this.cookieService.set(environment.UserGuid, response.UserGuid);
    this.cookieService.set(environment.BusinessPlanGuid, response.BusinessPlanGuid);
    this.cookieService.set(environment.ProductGuid, response.ProductGuid);
    this.cookieService.set(environment.CompanyGuid, response.CompanyGuid);
    this.updateCommonService.UpdateCompanyData();
  }
}
